import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { ScrollText, ListFilter, Activity } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../ui';
import { PageHeader } from '../features/shared/PageHeader';
import { InlineBanner } from '../features/shared/InlineBanner';
import { ChangesViewer } from '../features/audit/ChangesViewer';
import { useGqlToken, useQuery } from '../gql/hooks';
import { graphqlRequest } from '../gql/client';
import {
  AUDIT_LOG_QUERY,
  type AuditLogQueryResult,
  type AuditLogQueryVariables,
} from '../gql/operations';
import type { AuditLogEntry } from '../gql/types';
import { formatDateTime } from '../lib/format';

// Audit-log viewer (US-8.6.1). The `auditLog` query has NO server-side filter input (contract), so
// actor / action / targetType filtering happens client-side over the fetched entries. `changes`
// (JSON) renders as an expandable detail.
//
// Pagination (FIX-18): the backend `auditLog(first, after)` resolver now does honest keyset-cursor
// pagination — `after` advances past the prior `endCursor` instead of being ignored. So we fetch a
// first page, accumulate entries in local state, and expose a "Load more" control driven by the
// connection's `pageInfo` (hasNextPage / endCursor). Client-side filtering runs over whatever has
// been loaded so far.

const PAGE_SIZE = 50;

interface ClientFilter {
  actor: string;
  action: string;
  targetType: string;
}

const EMPTY_FILTER: ClientFilter = { actor: '', action: '', targetType: '' };

/** Apply the client-side filter (case-insensitive substring) over the fetched entries. */
function applyFilter(entries: AuditLogEntry[], filter: ClientFilter): AuditLogEntry[] {
  const actor = filter.actor.trim().toLowerCase();
  const action = filter.action.trim().toLowerCase();
  const targetType = filter.targetType.trim().toLowerCase();
  return entries.filter((e) => {
    if (actor && !e.actor.toLowerCase().includes(actor)) return false;
    if (action && !e.action.toLowerCase().includes(action)) return false;
    if (targetType && !e.targetType.toLowerCase().includes(targetType)) return false;
    return true;
  });
}

export function AuditLogPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const baseId = useId();
  const token = useGqlToken();

  const [filter, setFilter] = useState<ClientFilter>(EMPTY_FILTER);

  // Accumulated entries + keyset cursor state (seeded by the first page, extended by "Load more").
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const page = useQuery<AuditLogQueryResult, AuditLogQueryVariables>(AUDIT_LOG_QUERY, {
    first: PAGE_SIZE,
  });

  // Seed local state from the first-page query whenever it resolves.
  useEffect(() => {
    const conn = page.data?.auditLog;
    if (!conn) return;
    setEntries(conn.edges.map((e) => e.node));
    setEndCursor(conn.pageInfo.endCursor ?? null);
    setHasNextPage(conn.pageInfo.hasNextPage);
  }, [page.data]);

  // Load the next keyset page and append (de-duped by id). Honest `after` pagination (FIX-18).
  const loadMore = useCallback(async () => {
    if (!endCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await graphqlRequest<AuditLogQueryResult, AuditLogQueryVariables>(
        AUDIT_LOG_QUERY,
        { first: PAGE_SIZE, after: endCursor },
        { token },
      );
      const conn = data.auditLog;
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.id));
        const next = conn.edges.map((e) => e.node).filter((n) => !seen.has(n.id));
        return [...prev, ...next];
      });
      setEndCursor(conn.pageInfo.endCursor ?? null);
      setHasNextPage(conn.pageInfo.hasNextPage);
    } catch {
      // Load-more failures are non-fatal — the operator can retry. A first-page failure is
      // surfaced via the page-level error banner.
    } finally {
      setLoadingMore(false);
    }
  }, [endCursor, loadingMore, token]);

  const visible = useMemo(() => applyFilter(entries, filter), [entries, filter]);
  const hasFilter = filter.actor || filter.action || filter.targetType;

  const inputClass = `min-h-11 w-full rounded-xl border px-3 py-2.5 text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
    isDark
      ? 'border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
      : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
  }`;
  const labelClass = `mb-1.5 block text-[11px] font-medium uppercase tracking-wider ${
    isDark ? 'text-white/40' : 'text-slate-400'
  }`;

  return (
    <div>
      <PageHeader
        title="Audit log"
        description="Operator and system actions across the control plane. Every change is recorded."
        breadcrumbs={[{ label: 'Audit' }]}
      />

      {page.error && (
        <InlineBanner tone="error">
          Couldn’t load the audit log: {page.error.message}
        </InlineBanner>
      )}

      <section
        aria-label="Audit filters"
        className={`mb-6 rounded-2xl border p-5 ${
          isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-white shadow-sm'
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2
            className={`flex items-center gap-2 text-sm font-semibold ${
              isDark ? 'text-white/70' : 'text-slate-600'
            }`}
          >
            <ListFilter className="h-4 w-4" aria-hidden="true" />
            Filters
          </h2>
          {hasFilter && (
            <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor={`${baseId}-actor`} className={labelClass}>
              Actor
            </label>
            <input
              id={`${baseId}-actor`}
              type="text"
              value={filter.actor}
              onChange={(e) => setFilter((f) => ({ ...f, actor: e.target.value }))}
              placeholder="operator@…"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={`${baseId}-action`} className={labelClass}>
              Action
            </label>
            <input
              id={`${baseId}-action`}
              type="text"
              value={filter.action}
              onChange={(e) => setFilter((f) => ({ ...f, action: e.target.value }))}
              placeholder="provider.disabled"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={`${baseId}-target`} className={labelClass}>
              Target type
            </label>
            <input
              id={`${baseId}-target`}
              type="text"
              value={filter.targetType}
              onChange={(e) => setFilter((f) => ({ ...f, targetType: e.target.value }))}
              placeholder="Provider"
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {page.loading && entries.length === 0 ? (
        <div className="space-y-2" data-testid="audit-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="block" height={64} />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<ScrollText className="h-14 w-14" aria-hidden="true" />}
          title={hasFilter ? 'No matching audit entries' : 'No audit entries yet'}
          description={
            hasFilter
              ? 'No entries match these filters. Clear them to see the full log.'
              : 'Control-plane changes will appear here as they happen.'
          }
          {...(hasFilter
            ? { action: { label: 'Clear filters', onClick: () => setFilter(EMPTY_FILTER) } }
            : {})}
        />
      ) : (
        <ul className="space-y-3">
          {visible.map((entry) => (
            <li
              key={entry.id}
              data-testid="audit-entry"
              className={`rounded-2xl border p-4 ${
                isDark
                  ? 'border-white/[0.06] bg-white/[0.02]'
                  : 'border-slate-200 bg-white shadow-sm'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <span className="font-mono text-indigo-400">{entry.action}</span>
                  </p>
                  <p className={`mt-0.5 text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                    {entry.actor} · {entry.targetType}
                    {entry.targetId ? ` · ${entry.targetId}` : ''}
                  </p>
                </div>
                <time
                  className={`whitespace-nowrap text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}
                  dateTime={entry.insertedAt}
                >
                  {formatDateTime(entry.insertedAt)}
                </time>
              </div>
              <div className="mt-3">
                <ChangesViewer changes={entry.changes} id={`${baseId}-changes-${entry.id}`} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && entries.length > 0 && (
        <div className="mt-5 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadMore()}
            loading={loadingMore}
            leftIcon={<Activity className="h-3.5 w-3.5" aria-hidden="true" />}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
